/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {FixedSizeList as List} from 'react-window';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {SearchResultsCount} from '@ion/dls/src/components/search/SearchResultsCount';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {FilterHashMapKeyType} from './FilterHelpers';
import FilterSearchListItem from './FilterSearchListItem';
import {createFilterSearchResults} from './FilterSearchUtils';

export interface FilterSearchResultsProps {
  searchValue: string;
  optionType: string;
  optionItems: string[];
  optionData: FilterHashMapKeyType;
  tooMany?: number;
  clearSearchValue?: Function;
  onOptionSelected: Function;
  onGroupSelected: Function;
}

interface ListItem {
  index: number;
  style: any;
  data: any;
}

const useStyles = makeStyles(theme => ({
  activeCrumb : {
    color : dlsColor('grey1')
  },
  inactiveCrumb : {
    color : dlsColor('grey3')
  },
  result : {
    padding           : '5px 0px 20px',
    borderTop         : `solid 1px ${dlsColor('grey5')}`,
    '&:first-of-type' : {
      borderTop : 'none'
    }
  }
}));

/**
 * Displays the search results per the DLS based on passed props.
 * Results are displayed as either selectable items or selectable
 * items with a chevron (if the item is a parent of other items).
 * The onOptionSelected callback is called if the user clicks an
 * item; the onGroupSelected callback is called if the user clicks
 * the chevron on an item.
 *
 * ```typescript
 * import {FilterSearchResults} from '@ion/dls/src/filters';
 *
 * <FilterSearchResults searchValue='abc'
 *   optionType={optionType}
 *   optionItems={filterSelectedOptions}
 *   optionData={filterHashMap}
 *   tooMany={tooMany}
 *   clearSearchValue={clearSearchValue}
 *   onOptionSelected={handleOptionSelected}
 *   onGroupSelected={handleGroupSelected} />
 *
 * ```
 *
 */
export const FilterSearchResults = ({
  searchValue,
  optionType,
  optionItems,
  optionData,
  tooMany = 50,
  clearSearchValue,
  onOptionSelected,
  onGroupSelected
}: FilterSearchResultsProps) => {
  const classes = useStyles();
  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };
  const searchResultData = createFilterSearchResults({
    optionType,
    optionItems,
    optionData,
    crumbClasses
  });

  const height = {
    assets : 55,
    groups : 70
  };
  const isAssetFilter = optionType === translate('common:filters.assets');

  const renderList = ({
    index,
    style,
    data
  }: ListItem) => {
    const itemId = data[index][0];
    const item = optionData[itemId];
    if (item) {
      return (
        <div style={style}>
          <FilterSearchListItem item={item}
            itemId={itemId}
            searchValue={searchValue}
            clearSearchValue={clearSearchValue}
            onGroupSelected={onGroupSelected}
            onOptionSelected={onOptionSelected}/>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <SearchResultsCount searchValue={searchValue}
        searchedFilterData={optionItems}
        paddingLeft='0px'
        tooMany={optionItems.length >= tooMany} />
      <div>
        {
          searchResultData.map(({crumb, items}, index) => {
            const listHeight = items?.length * (isAssetFilter ? height.assets : height.groups);
            return (
              <div className={classes.result} key={`filterSearch-result-${index}`}>
                <Breadcrumb itemsAfterCollapse={2}>
                  {crumb}
                </Breadcrumb>
                <List style={{width: '100%', overflow: 'auto', overflowX: 'hidden'}}
                  height={listHeight}
                  itemSize={50}
                  itemData={items}
                  itemCount={items?.length}>
                  {renderList}
                </List>
              </div>
            );
          })
        }
      </div>
    </div>
  );
};

export default FilterSearchResults;
